<template>
  <v-dialog
    :value="open"
    max-width="1100"
    persistent
    content-class="deal-modal-dialog"
    @input="onDialogInput"
  >
    <v-card :loading="isLoadingDeal" class="deal-modal-card">
      <div class="deal-modal-layout">
        <div class="deal-modal-header">
          <div class="deal-modal-title">
            {{ modalTitle }}
          </div>
          <div v-if="isViewMode" class="deal-modal-tabs-bar">
            <v-tabs
              v-model="activeTab"
              class="deal-modal-tabs"
              color="primary"
              background-color="white"
            >
              <v-tabs-slider color="primary"></v-tabs-slider>
              <v-tab>{{ $t("DEALS.MODAL.TAB_INFORMATION") }}</v-tab>
              <v-tab>{{ $t("DEALS.MODAL.TAB_DOCUMENTS") }}</v-tab>
            </v-tabs>
          </div>
        </div>

        <div class="deal-modal-scroll">
          <v-tabs-items v-if="isViewMode" v-model="activeTab" class="deal-modal-tab-items">
            <v-tab-item>
              <DealModalInformation
                :deal="deal"
                :documents="initialDealDocuments"
                @updated="onDealPreviewUpdated"
                @field-updated="onDealFieldUpdated"
              />
            </v-tab-item>
            <v-tab-item>
              <DealModalDocuments
                :deal="deal"
                :deal-id="dealId"
                :initial-documents="initialDealDocuments"
                @documents-updated="initialDealDocuments = $event"
              />
            </v-tab-item>
          </v-tabs-items>

          <DealModalForm
            v-else
            ref="dealModalForm"
            :mode="mode"
            :deal-id="dealId"
            :documents="initialDealDocuments"
            :initial-deal-status-id="initialDealStatusId"
            :form-data="formData"
            :form-prefill="formPrefill"
            :selected-pipeline-id="selectedPipelineId"
            @update:form-data="formData = $event"
            @update:selected-pipeline-id="selectedPipelineId = $event"
            @update:is-submitting="isSubmitting = $event"
            @update:is-loading-options="isLoadingFormOptions = $event"
            @submitted="onDealSubmitted"
          />
        </div>

        <div class="deal-modal-footer">
          <v-spacer></v-spacer>
          <v-btn text @click="$emit('close')">{{ $t("GENERAL.LABELS.CLOSE_BUTTON") }}</v-btn>
          <v-btn
            v-if="canOpenEdit"
            color="primary"
            @click="startEdit"
          >
            {{ $t("GENERAL.LABELS.EDIT_BUTTON") }}
          </v-btn>
          <v-btn
            v-if="!isViewMode"
            color="success"
            :loading="isSubmitting"
            :disabled="isLoadingFormOptions"
            @click="$refs.dealModalForm.submit()"
          >
            {{ $t("GENERAL.LABELS.SAVE") }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { SHOW_SNACK } from "@/store/snack.module";
import { GET_DEAL } from "@/store/deals.module";
import DealModalInformation from "@/views/pages/deals/partials/DealModalInformation.vue";
import DealModalDocuments from "@/views/pages/deals/partials/DealModalDocuments.vue";
import DealModalForm from "@/views/pages/deals/partials/DealModalForm.vue";

export default {
  name: "DealModal",
  components: {
    DealModalInformation,
    DealModalDocuments,
    DealModalForm,
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    updateDealId: {
      type: [Number, String],
      default: null,
    },
    pipelineStageId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      activeTab: 0,
      mode: "create",
      deal: null,
      dealId: null,
      isSubmitting: false,
      isLoadingFormOptions: false,
      isLoadingDeal: false,
      selectedPipelineId: null,
      initialDealDocuments: [],
      initialDealStatusId: null,
      isInitializing: false,
      formData: this.getEmptyDealPayload(),
      formPrefill: this.getEmptyFormPrefill(),
    };
  },
  computed: {
    isViewMode() {
      return this.mode === "view";
    },
    isCompletedDeal() {
      return this.deal?.deal_status?.slug === "completed";
    },
    canModifyDeal() {
      return (
        this.hasPermissions("deals-edit") &&
        !!this.deal?.id &&
        (!this.isCompletedDeal || this.hasPermissions("deals-completed-edit"))
      );
    },
    canOpenEdit() {
      return this.isViewMode && this.activeTab === 0 && this.canModifyDeal;
    },
    modalTitle() {
      if (this.mode === "create") {
        return this.$t("DEALS.MODAL.ADD_TITLE");
      }
      if (this.mode === "edit") {
        return `${this.$t("DEALS.MODAL.EDIT_TITLE")}`;
      }
      return `${this.$t("DEALS.MODAL.VIEW_TITLE")}`;
    },
  },
  watch: {
    open: {
      immediate: true,
      async handler(newValue) {
        if (newValue) {
          await this.initializeModal();
        } else {
          this.resetState();
        }
      },
    },
    pipelineStageId() {
      if (this.open && !this.updateDealId) {
        this.formData.pipeline_stage_id = this.pipelineStageId || null;
      }
    },
  },
  methods: {
    async initializeModal() {
      if (this.isInitializing) {
        return;
      }

      this.isInitializing = true;

      try {
        if (this.updateDealId) {
          await this.prepareView(this.updateDealId);
          return;
        }
        await this.prepareCreate(this.pipelineStageId);
      } finally {
        this.isInitializing = false;
      }
    },
    getEmptyDealPayload() {
      return {
        name: "",
        organization_id: null,
        supplier_ids: [],
        client_ids: [],
        pipeline_stage_id: null,
        container_arrival_date: null,
        container_number: null,
        note: "",
        deal_type_id: null,
        incoterm_id: null,
        location: null,
        deal_status_id: null,
        is_paid: false,
        trade_side: "buying",
        owner_ids: [],
        products: [],
        loading_dates: [],
      };
    },
    getEmptyFormPrefill() {
      return {
        organization: null,
        pipeline: null,
        suppliers: [],
        clients: [],
        owners: [],
      };
    },
    onDialogInput(value) {
      if (!value) {
        this.$emit("close");
      }
    },
    startEdit() {
      if (!this.canModifyDeal) {
        return;
      }

      this.activeTab = 0;
      this.isSubmitting = false;
      this.isLoadingFormOptions = false;
      this.mode = "edit";
    },
    resetState() {
      this.activeTab = 0;
      this.mode = "create";
      this.deal = null;
      this.dealId = null;
      this.formData = this.getEmptyDealPayload();
      this.initialDealDocuments = [];
      this.initialDealStatusId = null;
      this.selectedPipelineId = null;
      this.formPrefill = this.getEmptyFormPrefill();
      this.isSubmitting = false;
      this.isLoadingFormOptions = false;
      this.isLoadingDeal = false;
      this.isInitializing = false;
    },
    async prepareCreate(stageId) {
      this.resetState();
      this.mode = "create";
      this.formData.pipeline_stage_id = stageId || null;
    },
    async prepareView(dealId) {
      this.resetState();
      this.mode = "view";
      this.dealId = dealId;
      this.isLoadingDeal = true;

      try {
        const payload = (await this.$store.dispatch(GET_DEAL, dealId)) || {};
        this.applyDealPayload(payload, dealId);
        return true;
      } catch (error) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.UNEXPECTED_ERROR"),
          type: "error",
        });
        return false;
      } finally {
        this.isLoadingDeal = false;
      }
    },
    onDealSubmitted({ deal, isUpdate }) {
      this.isSubmitting = false;
      this.isLoadingFormOptions = false;
      this.mode = "view";
      this.applyDealPayload(deal, this.dealId);
      this.$emit(isUpdate ? "updated" : "saved", deal);
    },
    onDealPreviewUpdated(deal) {
      this.applyDealPayload(deal, this.dealId);
      this.$emit("updated", deal);
    },
    onDealFieldUpdated(deal) {
      this.applyDealPayload(deal, this.dealId);
    },
    applyDealPayload(payload, fallbackDealId = null) {
      this.deal = payload;
      this.dealId = payload.id || fallbackDealId;
      this.formData = {
        name: payload.name || "",
        organization_id: payload.organization?.id || null,
        supplier_ids: (payload.suppliers || []).map((item) => Number(item.id)),
        client_ids: (payload.clients || []).map((item) => Number(item.id)),
        pipeline_stage_id: payload.pipeline_stage?.id || null,
        container_arrival_date: payload.container_arrival_date || null,
        container_number: payload.container_number || null,
        note: payload.note || "",
        deal_type_id: payload.deal_type?.id || null,
        incoterm_id: payload.incoterm?.id || null,
        location: payload.location || null,
        deal_status_id: payload.deal_status?.id || null,
        is_paid: !!payload.is_paid,
        trade_side: payload.trade_side === "selling" ? "selling" : "buying",
        owner_ids: (payload.owners || []).map((item) => Number(item.id)),
        products: (payload.products || []).map((item) => ({
          id: item.id,
          meat_id: item.meat?.id || null,
          description_ids: (item.descriptions || []).map((description) => description.id),
        })),
        loading_dates: (payload.loading_dates || []).map((item) => ({
          id: item.id,
          date: item.date || null,
        })),
      };
      this.initialDealDocuments = Array.isArray(payload.documents) ? payload.documents : [];
      this.initialDealStatusId = payload.deal_status?.id || null;

      const pipeline = payload.pipeline_stage?.pipeline || null;
      this.formPrefill = {
        organization: payload.organization || null,
        pipeline: pipeline || null,
        suppliers: Array.isArray(payload.suppliers) ? payload.suppliers : [],
        clients: Array.isArray(payload.clients) ? payload.clients : [],
        owners: Array.isArray(payload.owners) ? payload.owners : [],
      };

      this.selectedPipelineId = pipeline?.id || null;
    },
  },
};
</script>

<style scoped>
.deal-modal-card {
  height: 100%;
  overflow: hidden;
}

.deal-modal-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.deal-modal-header {
  flex: 0 0 auto;
  background: #fff;
}

.deal-modal-title {
  padding: 16px 24px 12px;
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.5;
  word-break: break-word;
}

.deal-modal-tabs-bar {
  padding: 0 16px;
  border-bottom: 1px solid #e3e6ef;
}

.deal-modal-tabs ::v-deep .v-tabs-bar {
  background-color: #fff !important;
}

.deal-modal-tabs ::v-deep .v-tab {
  text-transform: none;
  letter-spacing: normal;
  font-size: 1rem;
  font-weight: 500;
  min-width: 140px;
  color: #7f8899;
}

.deal-modal-tabs ::v-deep .v-tab--active {
  color: #1976d2;
}

.deal-modal-tabs ::v-deep .v-tabs-slider {
  height: 3px;
  background-color: #1976d2;
}

.deal-modal-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 24px;
  -webkit-overflow-scrolling: touch;
}

.deal-modal-tab-items ::v-deep .v-window {
  overflow: visible;
}

.deal-modal-tab-items ::v-deep .v-window__container {
  height: auto !important;
}

.deal-modal-tab-items ::v-deep .v-window-item {
  height: auto !important;
}

.deal-modal-footer {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid #e3e6ef;
  background: #fff;
}
</style>

<style>
.deal-modal-dialog {
  height: calc(100vh - 96px);
  max-height: calc(100vh - 96px);
  overflow: hidden;
}

.deal-modal-dialog > .v-card {
  height: 100%;
}
</style>
